import React from "react"
import PropTypes from "prop-types"
import Link from "components/elements/link"
import { optimizeContentfulImage } from "utils/optimizeContentfulImage"
import "./clinicInsurance.scss"

// Shared across every clinic page, the same photo the design uses.
const INSURANCE_PHOTO = "/images/insurance-support.jpg"

const PAYMENT_OPTIONS_PATH = "/get-started/payment-options"

// This band reads the same on every clinic page, so the copy lives here rather
// than in each clinic's Contentful entry.
const HEADING = "We make insurance for autism therapy simple."
const BODY =
  "Navigating insurance for autism services can be overwhelming. We handle coverage, eligibility, and authorizations, so your family can start care sooner and focus on what matters."

// Reassurance chips shown beside the photo. Fixed copy, the same promise on
// every clinic page, so it is not editorial content.
const ASSURANCES = [
  { label: "Coverage Verified", tone: "blue" },
  { label: "Insurance Eligible", tone: "peach" },
  { label: "Authorizations Secured", tone: "green" },
]

const CheckIcon = () => (
  <svg
    aria-hidden="true"
    className="ClinicInsurance__check"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="m8.5 12.2 2.4 2.4 4.6-5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
)

/**
 * Pair each payer with its uploaded logo, dropping the ones we do not have
 * artwork for yet. Skipping is deliberate: the wall fills in as logos are
 * uploaded, rather than showing a gap or a placeholder in the meantime.
 */
const LOCAL_LOGOS = "/images/insurers"

const withLogos = (payers, logoAssets) =>
  (payers || [])
    .map(payer => {
      // Contentful wins, so the artwork can be changed without a deploy.
      const asset = (logoAssets || []).find(item => item?.title === payer.logo)
      if (asset?.file?.url) {
        return {
          label: payer.label,
          url: optimizeContentfulImage(asset.file.url, {
            quality: 90,
            width: 420,
          }),
        }
      }

      // No artwork yet. The plan is still accepted, so name it rather than
      // drop it; omitting an approved payer understates coverage.
      if (!payer.image) return { label: payer.label, name: true }

      return {
        label: payer.label,
        // The 2x file keeps the mark crisp on retina at this small size.
        srcSet: `${LOCAL_LOGOS}/${payer.image}.png 1x, ${LOCAL_LOGOS}/${payer.image}@2x.png 2x`,
        url: `${LOCAL_LOGOS}/${payer.image}.png`,
      }
    })
    .filter(Boolean)

const LogoWall = ({ items }) => (
  <ul className="ClinicInsurance__logos">
    {items.map(item => (
      <li
        className={`ClinicInsurance__logo${
          item.name ? " ClinicInsurance__logo--name" : ""
        }`}
        key={item.url || item.label}
      >
        {item.name ? (
          item.label
        ) : (
          <img alt={item.label} src={item.url} srcSet={item.srcSet} />
        )}
      </li>
    ))}
  </ul>
)

LogoWall.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.bool,
      srcSet: PropTypes.string,
      url: PropTypes.string,
    })
  ),
}

const ClinicInsurance = ({ fallbackLogos, insurers, logoAssets }) => {
  const national = (fallbackLogos || []).map(item => ({
    label: item.title || "Insurance provider",
    url: optimizeContentfulImage(item.file?.url, { quality: 90, width: 420 }),
  }))

  // A centre with its own payer list shows those insurers. While the per-payer
  // logos are still being uploaded a region can resolve to nothing, so fall
  // back to the shared national set rather than leaving the band bare.
  const regional = insurers ? withLogos(insurers.allServices, logoAssets) : []
  const usingRegional = regional.length > 0

  const allServices = usingRegional ? regional : national
  const behavioralOnly = usingRegional
    ? withLogos(insurers.behavioralOnly, logoAssets)
    : []

  return (
    <section className="ClinicInsurance">
      <div className="container-v2 ClinicInsurance__inner">
        <div className="ClinicInsurance__main">
          <div className="ClinicInsurance__content">
            <h2 className="ClinicInsurance__title">{HEADING}</h2>
            <p className="ClinicInsurance__body">{BODY}</p>
            <Link className="ClinicInsurance__cta" to={PAYMENT_OPTIONS_PATH}>
              Learn more about payment options
            </Link>
          </div>

          <div className="ClinicInsurance__media">
            {INSURANCE_PHOTO && (
              <img
                alt=""
                className="ClinicInsurance__photo"
                src={INSURANCE_PHOTO}
              />
            )}
            <ul className="ClinicInsurance__assurances">
              {ASSURANCES.map(({ label, tone }) => (
                <li
                  className={`ClinicInsurance__assurance ClinicInsurance__assurance--${tone}`}
                  key={label}
                >
                  <CheckIcon />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {allServices.length > 0 && (
          <div className="ClinicInsurance__providers">
            <p className="ClinicInsurance__providers-label">
              Insurance providers accepted:
            </p>
            <LogoWall items={allServices} />
          </div>
        )}

        {/* The plan lists draw a line between full coverage and behavioral
            health only. Collapsing the two would overstate coverage. */}
        {behavioralOnly.length > 0 && (
          <div className="ClinicInsurance__providers">
            <p className="ClinicInsurance__providers-label">
              Accepted for behavioral health services only:
            </p>
            <LogoWall items={behavioralOnly} />
          </div>
        )}
      </div>
    </section>
  )
}

ClinicInsurance.defaultProps = {
  fallbackLogos: [],
  insurers: null,
  logoAssets: [],
}

const PayerShape = PropTypes.shape({
  image: PropTypes.string,
  label: PropTypes.string,
  logo: PropTypes.string,
})

ClinicInsurance.propTypes = {
  fallbackLogos: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.shape({ url: PropTypes.string }),
      title: PropTypes.string,
    })
  ),
  insurers: PropTypes.shape({
    allServices: PropTypes.arrayOf(PayerShape),
    behavioralOnly: PropTypes.arrayOf(PayerShape),
  }),
  logoAssets: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.shape({ url: PropTypes.string }),
      title: PropTypes.string,
    })
  ),
}

export default ClinicInsurance
